import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { observer } from "mobx-react";

import { getCurrentCulture, isCasinoHeader } from "@utils";
import { useRootAppStore } from "@hooks";
import { SearchInputField } from "@components-shared/desktop/inputs";
import { Button, FormButton } from "@components/buttons";
import { WebPlatform } from "@state/stores/types";
import CasinoSearchErrorPopup from "./CasinoSearchErrorPopup";

export default observer(function CasinoHeaderSearch() {
	const [isHover, setIsHover] = useState(false);

	const { pathname } = useLocation();

	const rootStore = useRootAppStore<WebPlatform>();

	const casinoSearchStore = rootStore.casinoSearchStore;

	const methods = useForm({
		mode: "onChange",
		defaultValues: {
			casinoSearch: casinoSearchStore.searchTerm,
		},
	});

	const showSearchIcon =
		pathname.includes(`/${getCurrentCulture()}/home`) ||
		pathname.includes(`/${getCurrentCulture()}/games`) ||
		pathname.includes(`/${getCurrentCulture()}/live-games`);

	if (!isCasinoHeader() || !showSearchIcon) return null;

	const handleKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const code = e.which;

		if (code === 13) {
			if (!casinoSearchStore.isSearchLongEnough) {
				return;
			}

			if (
				!casinoSearchStore.searchValidation.test(
					casinoSearchStore.searchTerm
				)
			) {
				return;
			}
			setIsHover(false);
			casinoSearchStore.onSubmit();
			methods.reset();
		}

		if (code === 27) {
			setIsHover(false);
			casinoSearchStore.resetSearch();
			methods.reset();
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		casinoSearchStore.onSearchTermChange(e.target.value);
	};

	const handleClose = () => {
		setIsHover(false);
		casinoSearchStore.resetSearch();
		methods.reset();
	};

	const onSuccess = (
		data: { casinoSearch: string },
		event: React.MouseEvent<HTMLFormElement>
	) => {
		event.stopPropagation();
		event.preventDefault();

		setIsHover(false);
		casinoSearchStore.onSearchTermChange(data.casinoSearch);
		casinoSearchStore.onSubmit();
		methods.reset();
	};

	if (casinoSearchStore.isSearchOpen) {
		return (
			<>
				<FormProvider {...methods}>
					<form
						onSubmit={methods.handleSubmit(onSuccess)}
						className="searchbar is-active"
					>
						<SearchInputField
							inputClassName="searchbar__input input"
							customErrorClassName="searchbar__validation message--validation field-validation-error"
							name="casinoSearch"
							validate={(value: string | undefined) => {
								if (value == null) {
									return "";
								}

								if (
									value.trim().length > 0 &&
									value.trim().length < 3
								) {
									casinoSearchStore.setIsErrorOpen(true);
									return "SEARCH.INFO";
								}

								if (
									value.trim().length > 0 &&
									!casinoSearchStore.searchValidation.test(
										value
									)
								) {
									casinoSearchStore.setIsErrorOpen(true);
									return "SEARCH.INFO";
								}

								casinoSearchStore.setIsErrorOpen(false);
							}}
							onKeyUp={handleKeyUp}
							onChange={handleChange}
							defaultValue={
								methods.getValues("casinoSearch") || ""
							}
							placeholder="HEADER.SEARCH_PLACEHOLDER"
							autoComplete="off"
							autoFocus
						/>
						<FormButton
							className="searchbar__btn btn--reset btn--ghost"
							iconOnly={true}
							iconClassName="u-icon--sml u-icon--search--alt u-color--text--primary"
						/>
						<Button
							type="button"
							className="searchbar__close btn--reset btn--ghost"
							iconOnly={true}
							iconClassName="u-icon--tny u-icon--close"
							onClick={() => {
								handleClose();
							}}
						/>
					</form>

					{casinoSearchStore.isErrorOpen && (
						<CasinoSearchErrorPopup />
					)}
				</FormProvider>
			</>
		);
	}

	return (
		<Button
			className="header__search btn--reset btn--ghost"
			iconOnly={true}
			iconClassName={
				"header__search__icon u-icon u-icon--sml u-icon--search--alt" +
				(isHover ? " u-color--brand--primary" : " u-color--core--snow")
			}
			onClick={casinoSearchStore.searchButtonOnClick}
			onMouseEnter={() => setIsHover(true)}
			onMouseLeave={() => setIsHover(false)}
		/>
	);
});
